/**
 * Rollout tab — progress per release, NOT a device list.
 *
 * "14 of 20 on target · 3 pinned · 1 stale" is the spec's example, but the
 * classifier this hub must agree with (`classifyDeviceRelease` /
 * `countFleetReleaseStatuses`, Task D4, `pos/fleet/fleetRows.ts`) has FIVE
 * mutually-exclusive buckets, not three. This tab shows all five — folding
 * any of them would break the one property that makes these numbers
 * trustworthy: they always sum to the fleet total. See `rolloutProgress.ts`.
 *
 * Each bucket is a link, never a table — the device stays managed in exactly
 * one place, the Fleet tab of `/pos-config`, which understands the same
 * `?status=` filter these links carry.
 *
 * Devices come straight from device-config-service (`GET /devices` for the
 * registered device types, then `GET /devices/{type}/status` and
 * `GET /devices/{type}/resolved-targets` per type — the same calls
 * `BrandingTab.tsx` makes for the Fleet table), so this tab and `/pos-config`
 * are reading the identical source of truth, not a second copy. A device whose
 * target could not be resolved keeps `targetVersion` absent and lands in
 * `unknown` — never a fabricated `on-target`.
 *
 * COMPLETENESS (Task D7): these counts are only trustworthy because the
 * buckets sum to the TRUE fleet total. When a device type's status read fails
 * its devices are missing from that total, so the tab must say so — an
 * operator reading "17 of 20 on target" cannot otherwise tell that a whole
 * device type is missing from the denominator. Partial results are still
 * shown (they are useful), but never labelled as complete.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Server } from 'lucide-react';
import { deviceConfigApi } from '../../services/deviceConfigApi';
import { deviceHealthApi } from '../../services/deviceHealthApi';
import { rolloutProgress, type RolloutBucket } from './rolloutProgress';
import { fleetTargetKey, resolvedTargetMapFromEntries, type ClassifiableDevice, type ResolvedTargetMap } from '../pos/fleet/fleetRows';
import { RubyLoader } from '../../components/RubyLoader';
import BuildHealthCard from './BuildHealthCard';
import { liveVersions, toBuildRows, type BuildHealthRow } from './buildHealthModel';

/** What could not be read this cycle. Empty arrays mean the counts are complete. */
interface LoadGaps {
  /** Device types whose `/status` read failed — their devices are MISSING from the total. */
  missingTypes: string[];
  /** Device types whose `/resolved-targets` read failed — their devices count, but as `unknown`. */
  targetlessTypes: string[];
  /**
   * Device types whose read SUCCEEDED but returned only part of the fleet
   * (the server said `truncated`). Without this the tab reads "50 on target ·
   * 1 unknown" from a bound, not from the fleet — a partial presented as
   * complete, which is the one thing these counts must never be.
   */
  truncatedTypes: string[];
  /** How many device types were attempted, for "N of M". */
  totalTypes: number;
}

const BUCKET_TONE: Record<RolloutBucket['status'], string> = {
  'on-target': 'border-emerald-300 bg-emerald-50 text-emerald-700',
  behind: 'border-amber-300 bg-amber-50 text-amber-700',
  pinned: 'border-rose-300 bg-rose-50 text-rose-700',
  stale: 'border-stone-300 bg-stone-100 text-stone-500',
  unknown: 'border-stone-300 bg-stone-100 text-stone-400',
};

const BucketCard: React.FC<{ bucket: RolloutBucket; total: number; incomplete?: boolean }> = ({ bucket, total, incomplete }) => (
  <Link
    to={bucket.href}
    className={`flex flex-col gap-1 rounded-xl border p-4 min-h-[44px] transition-colors hover:brightness-95 ${BUCKET_TONE[bucket.status]}`}
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm font-semibold">{bucket.label}</span>
      <span className="text-2xl font-bold tabular-nums">{bucket.count}</span>
    </div>
    <div className="text-[11px] opacity-80">{bucket.description}</div>
    {total > 0 && (
      <div className="text-[11px] font-medium opacity-70">
        {bucket.count} of {total} {incomplete ? 'loaded devices (partial) →' : 'devices →'}
      </div>
    )}
  </Link>
);

const RolloutTab: React.FC = () => {
  const [devices, setDevices] = useState<ClassifiableDevice[] | null>(null);
  const [gaps, setGaps] = useState<LoadGaps>({ missingTypes: [], targetlessTypes: [], truncatedTypes: [], totalTypes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buildRows, setBuildRows] = useState<BuildHealthRow[]>([]);
  const [buildLoading, setBuildLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reg = await deviceConfigApi.listDevices();
      const deviceTypes: string[] = Array.from(
        new Set((reg.devices || []).map((d: any) => d?.deviceType).filter((t: unknown): t is string => typeof t === 'string' && t.length > 0)),
      );

      const [statusResults, targetResults] = await Promise.all([
        Promise.allSettled(deviceTypes.map((t) => deviceConfigApi.listDeviceStatus(t))),
        Promise.allSettled(deviceTypes.map((t) => deviceConfigApi.listResolvedTargets(t))),
      ]);

      // deviceId → resolved target, built by the SAME pure mapper the Fleet
      // table uses, so both views agree about every device.
      const targets: ResolvedTargetMap = {};
      const targetlessTypes: string[] = [];
      const truncatedTypes: string[] = [];
      targetResults.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          // Keyed by deviceType + deviceId: `deviceId` is only unique within a
          // type, so accumulating several types under a bare deviceId would
          // overwrite one type's target with another's and judge a device
          // against a build it can never run.
          resolvedTargetMapFromEntries(r.value?.targets, targets, r.value?.deviceType || deviceTypes[i]);
          if ((r.value as any)?.truncated) truncatedTypes.push(deviceTypes[i]);
        } else {
          targetlessTypes.push(deviceTypes[i]);
        }
      });

      const merged: ClassifiableDevice[] = [];
      const missingTypes: string[] = [];
      statusResults.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          if ((r.value as any)?.truncated && !truncatedTypes.includes(deviceTypes[i])) truncatedTypes.push(deviceTypes[i]);
          for (const d of r.value.devices || []) {
            const deviceId = typeof d?.deviceId === 'string' ? d.deviceId : '';
            const deviceType = typeof d?.deviceType === 'string' && d.deviceType ? d.deviceType : deviceTypes[i];
            merged.push({
              appVersion: d?.appVersion ?? undefined,
              pinnedVersion: d?.pinnedVersion ?? undefined,
              // Absent when the server resolved nothing (or could not be
              // asked): the classifier then lands the device in `unknown`,
              // never a fabricated `on-target`.
              targetVersion: (deviceId && targets[fleetTargetKey(deviceType, deviceId)]?.version) || undefined,
              updatedAt: d?.updatedAt ?? null,
            });
          }
        } else {
          missingTypes.push(deviceTypes[i]);
        }
      });

      if (missingTypes.length > 0 && merged.length === 0 && deviceTypes.length > 0) {
        throw new Error(`Could not load device status for: ${missingTypes.join(', ')}`);
      }
      setGaps({ missingTypes, targetlessTypes, truncatedTypes, totalTypes: deviceTypes.length });
      setDevices(merged);
    } catch (e: any) {
      setError(e?.message || 'Failed to load rollout progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build health, per live app version (spec §9 placement 3) — a signal for
  // the promote decision, sitting above the rollout buckets. A failed read
  // for one build becomes `no-data` for that build (never a fabricated
  // "healthy"); if device-health-service is unreachable entirely (e.g. no
  // Fleet Monitoring grant), every row is `no-data` and the buckets below
  // still work — this card never blocks the rest of the tab.
  useEffect(() => {
    if (!devices) return;
    let live = true;
    const versions = liveVersions(devices);
    setBuildLoading(true);
    Promise.all(versions.map((v) => deviceHealthApi.summary({ scope: 'version', id: v.version, window: '7d' }).catch(() => null)))
      .then((sums) => {
        if (!live) return;
        setBuildRows(toBuildRows(versions, Object.fromEntries(versions.map((v, i) => [v.version, sums[i]]))));
      })
      .finally(() => { if (live) setBuildLoading(false); });
    return () => { live = false; };
  }, [devices]);

  if (loading && devices === null) {
    return (
      <RubyLoader variant="page" label="Loading rollout progress…" />
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
        <p className="text-sm text-stone-700 font-medium mb-1">Could not load rollout progress</p>
        <p className="text-xs text-stone-500 mb-4">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-700"
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  const progress = rolloutProgress(devices || []);
  // "Incomplete" covers BOTH a device type that failed to load and one the
  // server truncated: in either case the buckets do not sum to the true fleet,
  // and labelling the total "across every registered type" would be a lie.
  const incomplete = gaps.missingTypes.length > 0 || gaps.truncatedTypes.length > 0;

  // A partial load must never read as a complete one. Rendered ABOVE the
  // buckets (and above the "no devices" state, which is otherwise a lie when
  // the only type that has devices is the one that failed).
  const missing = gaps.missingTypes.length > 0;
  const gapNotice = (incomplete || gaps.targetlessTypes.length > 0) && (
    <div
      role="status"
      className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
    >
      <AlertTriangle size={15} className="mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        {missing && (
          <p className="font-semibold">
            Counts are incomplete — {gaps.missingTypes.length} of {gaps.totalTypes} device
            type{gaps.totalTypes === 1 ? '' : 's'} could not be loaded
            ({gaps.missingTypes.join(', ')}). Devices of {gaps.missingTypes.length === 1 ? 'that type are' : 'those types are'} missing
            from every bucket and from the total below.
          </p>
        )}
        {gaps.truncatedTypes.length > 0 && (
          <p className="font-semibold">
            The server returned only PART of the fleet for {gaps.truncatedTypes.length} device
            type{gaps.truncatedTypes.length === 1 ? '' : 's'} ({gaps.truncatedTypes.join(', ')}) —
            the devices past that cut are missing from every bucket and from the total below.
            A device is not "unknown" because of this; it is simply not counted.
          </p>
        )}
        {gaps.targetlessTypes.length > 0 && (
          <p>
            Resolved targets unavailable for {gaps.targetlessTypes.length} device
            type{gaps.targetlessTypes.length === 1 ? '' : 's'} ({gaps.targetlessTypes.join(', ')}) —
            those devices are counted, but as Unknown rather than assumed on target.
          </p>
        )}
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 min-h-[44px] rounded-lg bg-stone-800 text-white text-xs font-medium hover:bg-stone-700"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    </div>
  );

  if (progress.isEmpty) {
    return (
      <div className="space-y-3">
        {gapNotice}
        <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
          <Server size={32} className="mx-auto text-stone-300 mb-3" />
          <p className="text-sm font-semibold text-stone-700 mb-1">No devices</p>
          <p className="text-xs text-stone-500 max-w-md mx-auto">
            {incomplete
              ? 'No devices were returned by the device types that did load. Devices may exist on the types above that could not be read.'
              : 'No device has ever checked in, on any registered device type. Rollout progress appears here automatically once devices start reporting — there is nothing to configure on this page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {gapNotice}
      <div className="text-xs text-stone-400">
        {missing
          ? `${progress.total} device${progress.total === 1 ? '' : 's'} across the ${gaps.totalTypes - gaps.missingTypes.length} of ${gaps.totalTypes} device types that loaded · this is a partial fleet · click a bucket to see the devices in it`
          : incomplete
            ? `${progress.total} device${progress.total === 1 ? '' : 's'} loaded · the server returned only part of the fleet · this is a partial fleet · click a bucket to see the devices in it`
            : `${progress.total} device${progress.total === 1 ? '' : 's'} across every registered type · click a bucket to see the devices in it`}
      </div>
      <BuildHealthCard rows={buildRows} loading={buildLoading} />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {progress.buckets.map((b) => <BucketCard key={b.status} bucket={b} total={progress.total} incomplete={incomplete} />)}
      </div>
    </div>
  );
};

export default RolloutTab;
